import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Github, Linkedin, Mail, Download } from "lucide-react";
import SocialButton from "@/components/ui/social-button";

const Hero = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
    }> = [];

    for (let i = 0; i < 100; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2,
      });
    }

    const animate = () => {
      ctx.fillStyle = "rgba(15, 15, 15, 0.05)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0, 217, 255, 0.6)";
        ctx.fill();
      });

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
      />
      
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background pointer-events-none" />

      <div className="relative z-10 container mx-auto px-6 text-center animate-fade-in">
        <div className="mb-6 flex justify-center gap-4">
          <SocialButton
            href="https://github.com/lucasfer01"
            label="GitHub"
          >
            <Github className="w-5 h-5" />
          </SocialButton>
          <SocialButton
            href="https://www.linkedin.com/in/lucasfernandez-dev/"
            label="LinkedIn"
          >
            <Linkedin className="w-5 h-5" />
          </SocialButton>
          <SocialButton
            href="mailto:lucasferdev@gmail.com"
            label="Email"
          >
            <Mail className="w-5 h-5" />
          </SocialButton>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-up">
          Hi, I'm <span className="text-gradient">Lucas</span> 👋
        </h1>
        
        <p className="text-xl md:text-2xl text-muted-foreground mb-4 max-w-3xl mx-auto animate-slide-up" style={{ animationDelay: "0.1s" }}>
          Full Stack Developer crafting performant web apps with{" "}
          <span className="text-primary font-mono">React</span>,{" "}
          <span className="text-primary font-mono">Node.js</span> &{" "}
          <span className="text-primary font-mono">TypeScript</span>
        </p>

        <p className="text-base md:text-lg text-muted-foreground/80 mb-8 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: "0.15s" }}>
          Building performant and scalable digital products with clean architecture
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: "0.2s" }}>
          <Button
            size="lg"
            onClick={() => scrollToSection("projects")}
            className="bg-primary text-primary-foreground hover:glow-effect transition-all font-medium"
          >
            View My Work
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => scrollToSection("contact")}
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all"
          >
            Contact Me
          </Button>
          <a
            href="/assets/Lucas_Fernandez_Fullstack.pdf"
            download="Lucas_Fernandez_Fullstack_Javascript.pdf"
            aria-label="Download resume PDF"
          >
            <Button
              size="lg"
              variant="outline"
              className="border-muted-foreground/50 text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/10 transition-all gap-2 w-full sm:w-auto"
            >
              <Download className="w-4 h-4" />
              Download Resume
            </Button>
          </a>
        </div>

        <div className="mt-16 flex items-center justify-center gap-4 text-sm text-muted-foreground animate-slide-up" style={{ animationDelay: "0.3s" }}>
          <span className="w-12 h-px bg-border"></span>
          <span>📍 Argentina • Remote-ready</span>
          <span className="w-12 h-px bg-border"></span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
